import { INSPIRATION_LIMITS, type InspirationDTO } from "@zplanem/responses"
import { useEffect, useRef, useState } from "react"
import { useInspirationMutations } from "../../hooks/use-inspirations"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { MediaNavChevrons, useMediaModalKeys } from "./media-modal-chrome"

function DownloadIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-5 h-5"
			aria-hidden="true"
		>
			<path d="M12 3v12m0 0 4-4m-4 4-4-4" />
			<path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
		</svg>
	)
}

/**
 * Full-view modal for image/video inspirations: download + close top-right;
 * title on the left (click to edit, creator only); description in a scrollable
 * footer (click to edit). pdf/url kinds open in a new tab instead of here.
 */
export function InspirationModal({
	inspiration,
	propertyId,
	projectId,
	onClose,
	onPrev,
	onNext,
}: {
	inspiration: InspirationDTO
	propertyId: string
	projectId: string
	onClose: () => void
	/** ZPL-267: present only when another modal-viewable item exists. */
	onPrev?: () => void
	onNext?: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { update } = useInspirationMutations(propertyId, projectId)
	const editable = inspiration.canDelete
	const [title, setTitle] = useState(inspiration.title ?? "")
	const [editingTitle, setEditingTitle] = useState(false)
	const [description, setDescription] = useState(inspiration.description ?? "")
	const [editingDesc, setEditingDesc] = useState(false)
	const titleRef = useRef<HTMLInputElement>(null)
	const descRef = useRef<HTMLTextAreaElement>(null)

	useMediaModalKeys({ onClose, onPrev, onNext })

	useEffect(() => {
		if (editingTitle) titleRef.current?.focus()
	}, [editingTitle])
	useEffect(() => {
		if (editingDesc) descRef.current?.focus()
	}, [editingDesc])

	const isImage = inspiration.kind === "image"
	const isVideo = inspiration.kind === "video"

	function saveTitle() {
		setEditingTitle(false)
		const value = title.trim()
		if (value !== (inspiration.title ?? "")) {
			update.mutate({ id: inspiration.id, title: value })
		}
	}
	function saveDescription() {
		setEditingDesc(false)
		const value = description.trim()
		if (value !== (inspiration.description ?? "")) {
			update.mutate({ id: inspiration.id, description: value })
		}
	}

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label={title || t.projects.inspirations.modalTitle}
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={t.projects.inspirations.close}
				className="absolute inset-0 bg-black/70"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-lg border border-border bg-popover text-popover-foreground shadow-2xl overflow-hidden">
				<div className="flex items-start justify-between gap-3 p-4 border-b border-border">
					<div className="min-w-0 flex-1">
						{editable && editingTitle ? (
							<input
								ref={titleRef}
								type="text"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								onBlur={saveTitle}
								onKeyDown={(e) => {
									if (e.key === "Enter") e.currentTarget.blur()
								}}
								maxLength={INSPIRATION_LIMITS.titleMax}
								className="w-full rounded-lg bg-muted border border-border px-2 py-1 text-base font-bold text-foreground outline-none focus:border-ring"
							/>
						) : (
							<button
								type="button"
								disabled={!editable}
								onClick={() => setEditingTitle(true)}
								className="text-left text-base font-bold text-foreground truncate w-full disabled:cursor-default"
							>
								{title || t.projects.inspirations.titleLabel}
							</button>
						)}
					</div>
					<div className="flex items-center gap-1.5 shrink-0">
						{inspiration.sourceUrl ? (
							<a
								href={inspiration.sourceUrl}
								target="_blank"
								rel="noopener noreferrer"
								aria-label={t.projects.inspirations.download}
								className="rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-muted"
							>
								<DownloadIcon />
							</a>
						) : null}
						<button
							type="button"
							onClick={onClose}
							aria-label={t.projects.inspirations.close}
							className="rounded-md px-2.5 py-1 text-xl leading-none text-muted-foreground hover:text-foreground hover:bg-muted"
						>
							×
						</button>
					</div>
				</div>

				<div className="relative flex-1 min-h-0 overflow-auto bg-muted/30 flex items-center justify-center p-2">
					<MediaNavChevrons onPrev={onPrev} onNext={onNext} />
					{isImage && inspiration.sourceUrl ? (
						<img
							src={inspiration.sourceUrl}
							alt={title}
							className="max-w-full max-h-[60vh] object-contain"
						/>
					) : null}
					{isVideo && inspiration.sourceUrl ? (
						// biome-ignore lint/a11y/useMediaCaption: user-uploaded clip; no caption track available.
						<video
							src={inspiration.sourceUrl}
							controls
							className="max-w-full max-h-[60vh]"
						/>
					) : null}
				</div>

				<div className="border-t border-border p-4 max-h-40 overflow-y-auto">
					{editable && editingDesc ? (
						<textarea
							ref={descRef}
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							onBlur={saveDescription}
							maxLength={INSPIRATION_LIMITS.descriptionMax}
							rows={3}
							className="w-full rounded-lg bg-muted border border-border px-2 py-1 text-sm text-foreground outline-none focus:border-ring resize-none"
						/>
					) : (
						<button
							type="button"
							disabled={!editable}
							onClick={() => setEditingDesc(true)}
							className="text-left text-sm text-muted-foreground whitespace-pre-wrap w-full disabled:cursor-default"
						>
							{description ||
								(editable ? t.projects.inspirations.descriptionLabel : "")}
						</button>
					)}
				</div>
			</div>
		</div>
	)
}
